import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { Link as RouterLink } from 'react-router';
import { wazuhAPI } from 'src/api/mindtime/wazuhAPI';
import { RHFProvider } from 'src/common/rhf';
import { getPageTitle } from 'src/config/projectConfig';
import { SECURITY_ENDPOINTS_HREF } from 'src/mindtime/config/securityNav';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import {
  DEFAULT_SERVER_ADDRESS,
  DEPLOY_AGENT_GROUPS,
  DEPLOY_STEP_TITLES,
  LINUX_REQUIREMENTS,
  MINDTIME_CONTACT_URL,
} from './deployAgentConstants';
import type {
  DeployAgentFormState,
  OsFamily,
  PackageId,
} from './deployAgentTypes';
import DeployStepSection from './DeployStepSection';
import InfoBanner, { InfoBannerLink } from './InfoBanner';
import InstallCommandSteps from './InstallCommandSteps';
import OsPackageSelector from './OsPackageSelector';

const cardSx = {
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.06)',
  border: '1px solid #EDF2F7',
  borderRadius: '8px',
  backgroundColor: '#FFFFFF',
} as const;

const initialForm: DeployAgentFormState = {
  os: null,
  packageId: null,
  serverAddress: DEFAULT_SERVER_ADDRESS,
  rememberServerAddress: true,
  agentName: '',
  groups: [DEPLOY_AGENT_GROUPS[0]],
};

const deployAgentValidationSchema = object({
  agentName: string()
    .trim()
    .required('Agent name is required')
    .matches(/^[^\s]+$/, 'Agent name cannot contain spaces'),
});

const DeployNewAgent: FC = () => {
  const { isPreviewMode } = useEdrDataGate('endpoints');
  const [form, setForm] = useState<DeployAgentFormState>(initialForm);

  const methods = useForm<{ agentName: string }>({
    resolver: yupResolver(deployAgentValidationSchema) as any,
    defaultValues: { agentName: '' },
    mode: 'onChange',
  });

  const { watch, control, handleSubmit, setValue } = methods;
  const agentName = watch('agentName');

  useEffect(() => {
    if (agentName.includes(' ')) {
      setValue('agentName', agentName.replace(/\s+/g, ''), { shouldValidate: true });
    }
  }, [agentName, setValue]);

  // API-returned command list
  const [generatedCommands, setGeneratedCommands] = useState<string[] | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Track which command indices have been copied
  const [copiedMap, setCopiedMap] = useState<Record<number, boolean>>({});

  // Step completion logic
  const step1Complete = form.os != null && form.packageId != null;
  const step2Complete = agentName.trim().length > 0;

  const handleOsChange = (os: OsFamily) => {
    const firstPackage: PackageId =
      os === 'linux' ? 'linux-debian' : os === 'windows' ? 'windows' : 'macos';
    setGeneratedCommands(null);
    setCopiedMap({});
    setForm((prev) => ({
      ...prev,
      os,
      packageId: firstPackage,
    }));
  };

  // const OS_TYPE_MAP: Record<string, string> = {
  //   linux: 'linux',
  //   windows: 'windows',
  //   macos: 'macos',
  // };

  const handleGenerateCommands = async (data: { agentName: string }) => {
    if (!form.os || !form.packageId) return;

    setIsGenerating(true);
    setGeneratedCommands(null);
    setCopiedMap({});

    try {
      const generatedAgentName = data.agentName.trim() || 'unnamed-agent';
      const response = await wazuhAPI.createAgent({
        agentName: generatedAgentName,
        osType: form.packageId,
      });

      setGeneratedCommands(response.installCommands ?? []);
    } catch (error: any) {
      // Show the exact backend message when available
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to generate commands. Please try again.';
      toast.error(message);
      console.error('Deploy agent error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Security — Deploy new agent')}</title>
      </Helmet>

      <Box p={3}>
        <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 16 }}>
          <RouterLink
            to={SECURITY_ENDPOINTS_HREF}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              color: '#3182CE',
              textDecoration: 'none',
              fontSize: 13,
            }}
          >
            <ArrowBackIcon style={{ fontSize: 18 }} />
            Endpoints
          </RouterLink>
        </Box>

        <Typography style={{ fontSize: 22, fontWeight: 600, color: '#2D3748', marginBottom: 16 }}>
          Deploy new agent
        </Typography>

        <Card sx={cardSx} style={{ padding: '24px 28px' }}>
          <RHFProvider methods={methods} onSubmit={handleSubmit(handleGenerateCommands)}>
            {/* ── Step 1: Select OS & Package ───────────────────────────────────── */}
            <DeployStepSection
              stepNumber={1}
              title={DEPLOY_STEP_TITLES[0]}
              complete={step1Complete}
            >
              <OsPackageSelector
                os={form.os}
                packageId={form.packageId}
                onOsChange={handleOsChange}
                onPackageChange={(packageId) => {
                  setGeneratedCommands(null);
                  setCopiedMap({});
                  setForm((prev) => ({ ...prev, packageId }));
                }}
              />
              <InfoBanner variant="info">
                For additional systems and architectures, please {' '}
                <InfoBannerLink href={MINDTIME_CONTACT_URL}>contact us</InfoBannerLink>.
              </InfoBanner>
            </DeployStepSection>

            {/* ── Step 2: Agent Name ────────────────────────────────────────────── */}
            <DeployStepSection
              stepNumber={2}
              title={DEPLOY_STEP_TITLES[2]}
              complete={step2Complete}
              dimmed={!step1Complete}
            >
              <Typography style={{ fontSize: 13, color: '#718096', marginBottom: 12, lineHeight: 1.5 }}>
                By default, the deployment uses the agentName as the agent name. Optionally, you can
                use a different agent name in the field below.
              </Typography>
              <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 6 }}>
                <Typography style={{ fontSize: 13, fontWeight: 500, color: '#4A5568' }}>
                  Assign an agent name:
                </Typography>
                <HelpOutlineIcon style={{ fontSize: 16, color: '#A0AEC0' }} />
              </Box>
              <Controller
                name="agentName"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="Agent name"
                    disabled={!step1Complete}
                    error={Boolean(error)}
                    helperText={error?.message || ' '}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\s+/g, '');
                      field.onChange(value);
                      setGeneratedCommands(null);
                      setCopiedMap({});
                    }}
                  />
                )}
              />
              <InfoBanner variant="warning">
                The agent name must be unique. It can&apos;t be changed once the agent has been
                enrolled.
              </InfoBanner>
            </DeployStepSection>

            {/* ── Generate Commands Button ──────────────────────────────────────── */}
            <Box
              display="flex"
              justifyContent="flex-start"
              style={{ paddingLeft: 40, paddingBottom: 24 }}
            >
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!step1Complete || !step2Complete || isGenerating || isPreviewMode}
                style={{ minWidth: 180, height: 38, textTransform: 'none', fontWeight: 600 }}
                startIcon={
                  isGenerating ? <CircularProgress size={16} style={{ color: '#fff' }} /> : undefined
                }
              >
                {isGenerating ? 'Generating…' : 'Generate Commands'}
              </Button>
            </Box>
          </RHFProvider>

          {/* ── Dynamic Command Steps ─────────────────────────────────────────── */}
          {!generatedCommands ? (
            <DeployStepSection
              stepNumber={3}
              title={DEPLOY_STEP_TITLES[3]}
              complete={false}
              dimmed
              isLast
            >
              <InfoBanner variant="warning">
                Please complete the steps above and click <strong>Generate Commands</strong>.
              </InfoBanner>
            </DeployStepSection>
          ) : (
            <InstallCommandSteps
              commands={generatedCommands}
              startStepNumber={3}
              copiedMap={copiedMap}
              onCopy={(groupIndex) =>
                setCopiedMap((prev) => ({ ...prev, [groupIndex]: true }))
              }
              firstGroupHeader={
                form.os === 'windows' ? (
                  <Typography style={{ fontSize: 13, color: '#4A5568' }}>
                    Run the following in Windows PowerShell as Administrator.
                  </Typography>
                ) : null
              }
              firstGroupFooter={
                form.os === 'linux' ? (
                  <>
                    <Typography style={{ fontSize: 13, fontWeight: 600, color: '#2D3748' }}>
                      Requirements
                    </Typography>
                    <InfoBanner variant="info">
                      <Box component="ul" style={{ margin: 0, paddingLeft: 18 }}>
                        {LINUX_REQUIREMENTS.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </Box>
                    </InfoBanner>
                  </>
                ) : null
              }
            />
          )}
        </Card>
      </Box>
    </>
  );
};

export default DeployNewAgent;